"use client";

import dayjs from "dayjs";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React, { useMemo } from "react";

import { ErrorView } from "@/components/error-view";
import { useUpdateQuery } from "@/hooks/use-update-query";
import { Lang } from "@/i18n-config";
import { DateOption, getFirstDate } from "@/utils/date-options";
import { getTranslations } from "@/utils/i18n/get-translations";

import { ChartSkeletons } from "ui/widget/skeletons/Skeletons";

import { SwingInfo, TrendInfo, ValueInfo } from "../../services/symbol-content";
import { getBacktestActionData } from "../data/backtest-chart-action-data";
import { getBacktestRoiData } from "../data/backtest-chart-roi-data";
import { useBacktestActions } from "../services/backtest-actions";
import { useCumulativeROI } from "../services/cumulative-roi";
import { useSymbolPrices } from "../services/symbol-prices";
import { BacktestDateButtons } from "./BacktestDateButtons";

type BacktestChartProps = {
  lang: Lang;
  symbol: string;
  oriented: "value" | "swing" | "trend";
  strategy: ValueInfo | SwingInfo | TrendInfo;
};

const ULineChart = dynamic(
  () => import("ui/analysis/u-line-chart").then((mod) => mod.ULineChart),
  { ssr: false }
);

export const BacktestCharts = React.memo<BacktestChartProps>(
  ({ lang, symbol, oriented, strategy }) => {
    const { t } = getTranslations(lang);
    const { updateQuery } = useUpdateQuery();

    const searchParams = useSearchParams();
    const dateOption = (searchParams.get("backtest-date-option") ??
      "All") as DateOption;

    const firstDate = dayjs(getFirstDate(dateOption));

    const {
      data: priceData,
      isLoading: isSymbolPriceLoading,
      isError: isSymbolPriceError,
    } = useSymbolPrices({ symbol });

    const {
      data: backtestActionsData,
      isLoading: isBacktestActionsLoading,
      isError: isBacktestActionsError,
    } = useBacktestActions({ symbol, oriented, strategy });

    const {
      data: cumulativeRoiData,
      isLoading: isCumulativeRoiLoading,
      isError: isCumulativeRoiError,
    } = useCumulativeROI({ symbol, oriented, strategy });

    const historicalData = useMemo(
      () =>
        getBacktestActionData({
          priceData,
          backtestActionsData,
          firstDate,
        }),
      [backtestActionsData, firstDate, priceData]
    );

    const cumulativeData = useMemo(
      () =>
        getBacktestRoiData({
          data: cumulativeRoiData,
          firstDate,
        }),
      [cumulativeRoiData, firstDate]
    );

    const isLoading =
      isSymbolPriceLoading ||
      isBacktestActionsLoading ||
      isCumulativeRoiLoading;

    const isError =
      isSymbolPriceError || isBacktestActionsError || isCumulativeRoiError;

    return isLoading ? (
      <LoadingView />
    ) : isError ? (
      <ErrorView className="min-h-[300px]">{t.error.serverError}</ErrorView>
    ) : (
      <>
        <BacktestDateButtons
          dateOption={dateOption}
          onChange={(option) => {
            updateQuery({ name: "backtest-date-option", value: option });
          }}
        />
        <div className="flex h-full w-full flex-col overflow-x-hidden">
          <p className="text-xs text-zinc-400">
            {t.analysis.backtest.historyAndEntryExit}
          </p>
          {historicalData[0].length !== 0 && (
            <div className="w-[108%] -translate-x-2 @lg:w-full @lg:translate-x-0">
              <ULineChart
                data={historicalData}
                lang={lang}
                height={250}
                dateLabel={t.analysis.shared.date}
                syncId="backtest"
                showSignal
                showLegend="live"
                dataInfo={[
                  {
                    label: t.analysis.shared.price,
                    valueFormatter: (value) => `$${value as number}`,
                    strokeColor: "#5A8FD6",
                    strokeWidth: 2,
                  },
                ]}
              />
            </div>
          )}
          <p className="text-xs text-zinc-400">
            {t.analysis.backtest.cumulativeROI}
          </p>
          {cumulativeData[0].length !== 0 && (
            <div className="w-[108%] -translate-x-2 @lg:w-full @lg:translate-x-0">
              <ULineChart
                data={cumulativeData}
                lang={lang}
                height={250}
                dateLabel={t.analysis.shared.date}
                syncId="backtest"
                showLegend="live"
                dataInfo={[
                  {
                    label: t.analysis.backtest.ROI,
                    valueFormatter: (value) => `${value as number}%`,
                    strokeColor: "#225A9F",
                    strokeWidth: 2,
                  },
                ]}
              />
            </div>
          )}
        </div>
      </>
    );
  }
);
BacktestCharts.displayName = "BacktestCharts";

const LoadingView = () => (
  <div className="flex w-full py-3">
    <div className="flex w-full flex-col gap-16">
      <ChartSkeletons />
      <ChartSkeletons />
    </div>
  </div>
);
